import { FC, MouseEvent, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { flattenNavItems } from 'routes/constants';
import { TNavGroupButtonProps } from './types';
import { isPathActive } from './utils';
import { renderNavMenuItems, toNavMenuItems } from '../NavMenuEntries';

export const NavGroupButton: FC<TNavGroupButtonProps> = ({
	name,
	items,
	locationPathname,
	onItemClick,
	forceOpen = false,
	groupId,
	getItemId,
}) => {
	// Use a state-based ref-callback so we can safely use the button element as
	// an anchor for the menu without reading a `ref.current` during render.
	const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
	const [internalAnchorEl, setInternalAnchorEl] = useState<null | HTMLElement>(null);

	const isOpen = Boolean(internalAnchorEl) || forceOpen;
	const anchorEl: HTMLElement | null = forceOpen ? buttonEl : internalAnchorEl;

	const isGroupActive = useMemo(
		() => flattenNavItems(items).some((item) => isPathActive(locationPathname, item.path)),
		[items, locationPathname],
	);

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setInternalAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		// While the dropdown is forced open by the tour we ignore close events
		// (e.g. backdrop click) so the user-controlled tour stays in charge.
		if (forceOpen) return;
		setInternalAnchorEl(null);
	};

	const handleItemClick = (path: string) => {
		if (!forceOpen) {
			setInternalAnchorEl(null);
		}
		onItemClick(path);
	};

	return (
		<>
			<Button
				id={groupId}
				ref={setButtonEl}
				onClick={handleOpen}
				endIcon={<KeyboardArrowDownIcon sx={{ ml: -0.5 }} />}
				sx={{
					color: isGroupActive ? '#5aa1bf' : 'primary',
					textTransform: 'none',
					fontWeight: 550,
					fontSize: '0.8rem',
					justifyContent: 'flex-start',
					lineHeight: '1.2rem',
					whiteSpace: 'nowrap',
				}}
			>
				{name}
			</Button>
			<Menu
				anchorEl={anchorEl}
				open={isOpen}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				{renderNavMenuItems({
					items: toNavMenuItems(items),
					locationPathname,
					onItemClick: handleItemClick,
					getItemId,
				})}
			</Menu>
		</>
	);
};
